import {EWedletEditMode, EWedletEditModeLabel, ICustomClipboard, IElementWedlet, IS_EltWedlet, IVirtualisableWedlet, IWedlet, WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {EFuzzyType, ISkAnnot, ISkAnnotDrawer, ISkStructDef, SkAnnotAttrUnknown, SkAnnotEltUnknown} from "/@lib@/edit/schema/schemaAnnots.js";
import {IJmlNode, IJmlObj, IJmlSet} from "/@lib@/commons/xml/jml.js";
import {DOM, ENodeType, JSX} from "/@lib@/commons/xml/dom.js";
import {IXAddr, XA} from "/@lib@/commons/xml/xAddr.js";
import {IWedMatcher, WED} from "/@back@/edit/wed/wedCore.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {Action, IAction} from "/@lib@/commons/actions.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {REG} from "/@lib@/commons/registry.js";
import {IDocBatch} from "/@lib@/edit/docHolder.js";
import {isWedletSingleElt} from "/@back@/edit/wed/wedlets/wedletSingleElt.js";
import {MSGMETA_noCleanup} from "/@lib@/edit/ot/houseOt.js";


/** Pour XML générique : input du nom d'un élement ou attribut. */
class BoxNameInput extends HTMLElement implements IElementWedlet, ISkAnnotDrawer, ICustomClipboard {

	wedlet: IWedlet;

	constructor() {
		super();
		this.ondblclick = this.onDblClick;
		this.addEventListener("keydown", this.onKeyDown);
		this.addEventListener("blur", this.onBlur);
		this.style.whiteSpace = "pre"; //obligatoire sinon le navigateur (chrome) trafique les espaces (32) et les espaces inséccables (160).
		//this.style.userSelect = "text"; //utile pour sélectionner le texte même si widget passé en readonly contentEditable=false.
		this.spellcheck = false;
		this.contentEditable = "true";
		this.appendChild(document.createTextNode(""));
	}

	onDblClick(ev: MouseEvent) {
		ev.preventDefault();
		ev.stopPropagation();
	}

	async onClipboard(act: "copy" | "cut" | "paste", ev?: Event, options?: Dict<any>): Promise<boolean> {
		const sel = DOMSH.findDocumentOrShadowRoot(this).getSelection();
		if (sel?.focusNode?.parentNode === this) {
			switch (act) {
			case "copy":
				if (sel.type === "Range") navigator.clipboard.writeText(sel.getRangeAt(0).cloneContents().textContent || "");
				break;
			case "cut":
				if (sel.type === "Range") navigator.clipboard.writeText(sel.getRangeAt(0).extractContents().textContent || "");
				break;
			case "paste":
				ev?.preventDefault(); //On réimpl le paste pour bloquer le paste natif qui prend l'html du clipboard, pas la forme txt pure.
				if (sel.type === "Range") sel.deleteFromDocument();
				const txt = await navigator.clipboard.readText();
				if (txt) {
					const node = document.createTextNode(txt);
					if (sel.rangeCount > 0) {
						const rg = sel.getRangeAt(0);
						rg.insertNode(node);
					} else {
						this.appendChild(node);
					}
					sel.setPosition(node, node.nodeValue.length);
				}
				break;
			}
			return true;
		}
		return false;
	}

	configWedletElt(tpl: Element, wedlet: IWedlet) {
		this.wedlet = wedlet;
		this.contentEditable = WEDLET.isWritableWedlet(wedlet) ? 'true' : 'false';
	}

	refreshBindValue(val: IJmlNode) {
		this.classList.toggle("virtual", this.wedlet.isVirtual());
		this.textContent = this.wedlet.wedNodeName || this.wedlet.model.nodeName;
	}

	setEditMode(mode: EWedletEditMode) {
		this.contentEditable = mode === EWedletEditMode.write ? 'true' : 'false';
		DOM.setAttr(this, "edit-mode", EWedletEditModeLabel[mode]);
	}

	onBlur(ev: Event) {
		//console.log("blur::::", ev);
		const newName = this.textContent;
		const oldName = this.wedlet.wedNodeName || this.wedlet.model.nodeName;
		//console.log("onBlur:::",oldName, "->", newName);
		if (oldName !== newName) {
			if (newName) {
				const isAttr = typeof this.wedlet.xaPart === 'string';
				const rule = this.wedlet.wedMgr.docHolder.getStruct(this.wedlet.wedAnchor, WEDLET.buildVirtualPath(this.wedlet));
				if (/^[A-Za-z_:][\w\-.:]*$/.test(newName) && rule?.structMatch(isAttr ? ENodeType.attribute : ENodeType.element, newName)) {
					if (this.wedlet.isVirtual()) {
						WEDLET.insertDatasFromDisplay(this.wedlet as IVirtualisableWedlet, null, null, newName);
					} else {
						if (isAttr) {
							const xa = this.wedlet.wedAnchor;
							this.wedlet.wedMgr.docHolder.newBatch().renameOrMoveAttr(xa, XA.append(XA.subXa(xa, -1), newName)).setMeta(MSGMETA_noCleanup, true).doBatch();
						} else {
							this.wedlet.wedMgr.docHolder.newBatch().renameElt(this.wedlet.wedAnchor, newName).setMeta(MSGMETA_noCleanup, true).doBatch();
						}
					}
				} else {
					POPUP.showNotifInfo("Ce nom n'est pas autorisé", this);
				}
			}
			//on restaure l'ancien nom en attendant l'exécution réelle (qui va remplacer ce noeud).
			this.textContent = oldName;
		}
	}

	onKeyDown(ev: KeyboardEvent) {
		//console.log("onKeyDown::::",ev.key, ev);
		switch (ev.key) {
		case 'Delete' :
		case 'Backspace' :
			ev.stopPropagation();
			break;
		case 'Enter':
		case ' ':
			//mono-line, keyword -> pas de saut de ligne ni d'espace.
			ev.preventDefault();
			//bloque remontée pour interférences avec le container box-collpase
			ev.stopImmediatePropagation();
			break;
		}
	}

	drawAnnot(annot: ISkAnnot): boolean {
		if (annot.type === SkAnnotEltUnknown.TYPE || annot.type === SkAnnotAttrUnknown.TYPE) this.classList.add("forbidden");
		return false;
	}

	eraseAnnot(annot: ISkAnnot): boolean {
		if (annot.type === SkAnnotEltUnknown.TYPE || annot.type === SkAnnotAttrUnknown.TYPE) this.classList.remove("forbidden");
		return false;
	}
}

window.customElements.define("box-nameinput", BoxNameInput);


/**
 * Bouton d'insertion de noeuds.
 *
 * Si l'elt n'a pas d'attribut skin, le widget est construit sans shadowRoot, avec son input en fils.
 * Pour usage en "surimpression", positionné en absolu dans son contexte (notamment pour éditeurs XML génériques).
 * - Double noeuds box-insert et button sinon bug de positionnement par défaut (en mode position:absolute du button).
 * - Le button n'est pas en shadowTree, car alors plus difficile d'ajuster la position du button dans le flot
 *    en fonction de chaque contexte.
 * - Tous les styles doivent être définis dans le shadow parent (ie du bind incluant ce box-insert).
 *
 * Si l'elt a un attribut "skin", alors l'input sera "auto-stylé" et le button sera dans le shadowTree.
 * Pour usage classique d'un point d'insertion dans le flot de la page.
 */
class BoxInsert extends HTMLElement implements IElementWedlet {

	wedlet: IWedlet;

	matcher: IWedMatcher;

	configWedletElt(tpl: Element, wedlet: IWedlet) {
		this.wedlet = wedlet;
		this.matcher = WED.buildMatcherVariants(tpl.getAttribute("select"));
		//DOM.setHidden(this, !WEDLET.isWritableWedlet(wedlet));
		let parentBtn = this as Node;
		if (tpl.hasAttribute("skin")) {
			WEDLET.installSkins(tpl, this.attachShadow(DOMSH.SHADOWDOM_INIT), wedlet, this.localName);
			parentBtn = this.shadowRoot;
		}
		const btn = parentBtn.appendChild(document.createElement("button"));
		btn.addEventListener("click", this._onClickInsert);
	}

	refreshBindValue(val: IJmlNode) {
	}

	setEditMode(mode: EWedletEditMode) {
		DOM.setAttr(this, "edit-mode", EWedletEditModeLabel[mode]);
	}

	_onClickInsert(this: HTMLButtonElement, ev: MouseEvent) {
		const comp = DOMSH.getFlatParentElt(this) as BoxInsert;
		if (comp.wedlet.isVirtual()) return;
		const wedMgr = comp.wedlet.wedMgr;
		if (!wedMgr.docHolder) return;
		const tw = document.createTreeWalker(DOMSH.findDocumentOrShadowRoot(comp) as any, NodeFilter.SHOW_ELEMENT);
		tw.currentNode = comp;
		let prev;
		let offset = 0;
		findOffset: while (prev = tw.previousNode()) {
			if (prev instanceof HTMLSlotElement) {
				const children = prev.assignedNodes();
				for (let i = children.length - 1; i >= 0; i--) {
					const ch = children[i];
					if (IS_EltWedlet(ch) && typeof ch.wedlet.xaPart === 'number') {
						offset = ch.wedlet.xaPart + 1;
						break findOffset;
					}
				}
			}
		}
		const wedletAnchor = XA.freeze(comp.wedlet.wedAnchor);
		const [structsNodes, structsAtts] = wedMgr.docHolder.getInsertableStructs(wedletAnchor, offset, null, true);
		const virtuals = WEDLET.findVirtualWedletsAfter(comp, WEDLET.findVirtualWedletsBefore(comp));
		const structNodesCount = WEDLET.cleanupStructList(structsNodes, virtuals, WEDLET.isTextBindBefore(comp) || WEDLET.isTextBindAfter(comp));
		const structAttsCount = WEDLET.cleanupStructList(structsAtts, virtuals);
		const actions: IAction<IWedlet>[] = [];
		const xa = XA.append(wedletAnchor, offset);
		if (structNodesCount > 0) for (let i = 0; i < structsNodes.length; i++) {
			const struct = structsNodes[i];
			if (struct && comp.matcher.matchStruct(struct)) actions.push(new InsertTag(struct, xa));
		}
		if (structAttsCount > 0) for (let i = 0; i < structsAtts.length; i++) {
			const struct = structsAtts[i];
			if (struct && comp.matcher.matchStruct(struct)) actions.push(new InsertTag(struct, xa));
		}
		if (actions.length > 0) {
			POPUP.showPopupActionsFromEvent({actions, actionContext: comp.wedlet, restoreFocus: this}, ev);
		} else {
			POPUP.showNotif(<i style="margin:.2em">Aucune insertion possible</i>, this, {autoHide: 500, noCloseBtn: true}, {initX: ev.pageX, initY: ev.pageY});
		}
		ev.stopImmediatePropagation();
		ev.preventDefault();
	}
}

/** Attention : skin utilisé uniquement si explicitement déclaré sur l'attribut. */
REG.reg.registerSkin('box-insert', 1, /* language=CSS */ `
	:host([edit-mode]) {
		display: none;
	}

	button {
		user-select: none;
		padding: 0 2px;
		border-radius: .2em;
		background: var(--insbtn-bg);
		color: var(--insbtn-color);
		border: none;
		font-size: var(--label-size);
		cursor: pointer;
	}

	button:before {
		content: "+";
	}

	button:hover {
		background: var(--insbtn-hover-bg);
	}

	:focus-visible {
		outline: var(--edit-box-focus);
	}
`);
window.customElements.define("box-insert", BoxInsert);

class InsertTag extends Action<IWedlet> {
	constructor(public struct: ISkStructDef, public xaInsert: IXAddr) {
		super();
	}

	getLabel() {return this.struct.structLabel || this.struct.structName}

	execute(ctx: IWedlet, ev?: Event) {
		if (isWedletSingleElt(ctx) && !ctx.element.isConnected) return; //détection d'une suppr concurrente
		const wedMgr = ctx.wedMgr;
		const batch: IDocBatch = wedMgr.docHolder.newBatch();
		let jml: IJmlSet = [];
		let atts: IJmlObj = Object.create(null);
		const partialName = this.struct.createContent(jml, atts);
		//console.log("insert:::", JSON.stringify(jml));
		if (typeof partialName === 'string') {
			//Insertion d'une struct générique
			//XXX à améliorer on devrait passer par un état intermédiaire pour demander le nom de l'objet à insérer...
			if (this.struct.structType === EFuzzyType.elements) {
				jml.push({'': partialName || "x"})
			} else if (this.struct.structType === EFuzzyType.attributes) {
				let nm = partialName || "x";
				const elt = XA.findDomContainer(this.xaInsert, wedMgr.docHolder.getDocument()) as Element;
				while (elt.hasAttribute(nm)) nm += 'x';
				atts[nm] = "";
			} else throw Error();
		}
		if (ctx.isVirtual()) WEDLET.insertDatasFromDisplay(ctx, batch);
		const attNames = atts ? Object.keys(atts) : null;
		if (attNames && attNames.length > 0) {
			if (attNames.length > 1 || jml.length > 0) {
				//On insert plusieurs noeuds (atts ou children)
				const xaParent = XA.freeze(XA.up(this.xaInsert));
				for (let i = 0; i < attNames.length; i++) {
					batch.setAttr(XA.append(xaParent, attNames[i]), atts[attNames[i]] || "");
				}
				if (jml.length > 0) batch.insertJml(this.xaInsert, jml);
			} else {
				//On insert un seul attribut
				batch.setAttr(XA.setAtDepth(this.xaInsert, -1, attNames[0]), atts[attNames[0]] || "")
			}
		} else {
			//On insert que des noeuds fils
			batch.insertJml(this.xaInsert, jml);
		}
		batch.doBatch();
	}
}